import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { UserSessionService } from 'src/app/pages/users/shared/user-session.service';
import { IFacility } from 'src/app/shared/models/IFacility';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { AuthorizeService } from '../auth/authorize.service';
import { ToastrService } from 'ngx-toastr';
import { IIDN } from 'src/app/shared/models/IIDN';
import { Router } from '@angular/router';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { LoggingService } from 'src/app/shared/services/logging.service';
import { INavMenu, MenuEnum } from './model/nav-menu.model';
import { NavMenuService } from './service/nav-menu.service';
import { UserMenuManagementService } from './service/user-menu-management.service';
import { ISessionInfo } from './model/session-info.model';
import { HSPARole } from 'src/app/shared/models/hspa-role.enum';
import { IUserSession } from 'src/app/shared/models/IUserSession';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.css']
})

export class NavMenuComponent implements OnInit, OnDestroy {
  navMenu?: INavMenu;
  navMenuSubscriptions: Subscription[] = [];
  userName = '';
  isLoading = false;
  resourceKeyValue: any;
  allIdns: IIDN[] = [];
  hospitals: IFacility[] = [];
  selectedHospital!: IFacility;
  selectedIdnID = 0;
  clientID = '';
  tenantCode = '';
  applicationNameFinalLabel = '';
  dashboardIDNName = '';
  dashboardFacilityName = '';
  subMenuclick: MenuEnum = MenuEnum.None;
  HasManageUserRole = false;
  changeButtonVisibility = false;
  hasIDNsAccessAndAdmin = false;
  facilityForm = this.fb.group({
    idn: ['', [Validators.required]],
    facilityId: ['', [Validators.required]]
  });

  constructor(
    private navMenuService: NavMenuService,
    private authorizeService: AuthorizeService,
    private resourcesService: ResourcesService,
    private userSessionService: UserSessionService,
    private fb: UntypedFormBuilder,
    public toastrService: ToastrService,
    private router: Router,
    private appSettings: AppSettingsService,
    private loggingService: LoggingService,
    private userMenuManagementService: UserMenuManagementService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.resourcesService.initializeResources();
    this.resourcesService.bsObject.subscribe(result => {
      if (result) {
        this.resourceKeyValue = result;
        this.setApplicationProductName();
      }
    });

    this.getUserSession();
    this.getNavMenu();

    //Get client Id from AppSettings
    this.clientID = this.appSettings.clientId;
    this.getSelectedMenu();
  }

  private getNavMenu(): void {
    const navMenu$ = this.navMenuService.getNavBarInfo().pipe(
      mergeMap((navMenu: any) => {
        this.isLoading = false;
        this.navMenu = navMenu;
        this.userName = navMenu.userName;
        this.hasIDNsAccessAndAdmin = navMenu.hasIDNsAccessAndAdmin;
        localStorage.setItem('username', navMenu.userName);
        localStorage.setItem('permission', navMenu.permission);
        const storedTenantId = Number(localStorage.getItem('tenantId'));
        if (storedTenantId < 1) {
          localStorage.setItem('tenantId', navMenu.tenantId);
        }
        const storedUserRole = localStorage.getItem('userRole');
        const navRoles = navMenu.roles.join(', ');
        if (navRoles !== storedUserRole) {
          localStorage.setItem('userRole', navRoles);
          localStorage.setItem('tenantId', navMenu.tenantId);
          localStorage.removeItem('facilityId');
        }

        const isClinicianUser = navMenu.roles.find(x => x == HSPARole.ClinicianUser);
        const isCustomerAdmin = navMenu.roles.find(x => x == HSPARole.CustomerSystemAdministrator);

        if (navMenu.roles.length == 1 && (isClinicianUser || isCustomerAdmin)) {
          this.HasManageUserRole = true;
        }
        return this.navMenuService.getIDNAndFacilitiesForCurrentUser();
      }),
      catchError(error => {
        this.showErrorNotification(error.message, 'Facilities');
        this.loggingService.errorLog(error.message);
        this.isLoading = false;
        return of([]);
      }),
      mergeMap((response: IIDN[]) => {
        if (response?.length > 0) {
          const tenantId = Number(localStorage.getItem('tenantId'));
          const facilityId = Number(localStorage.getItem('facilityId'));
          const currentIDN = response.find(idn => idn.id === tenantId);
          if (currentIDN) {
            this.selectedIdnID = currentIDN.id;
            this.dashboardIDNName = currentIDN.name;

            if (currentIDN.hospitalFacilities?.length > 0) {
              const selectedFacility = currentIDN.hospitalFacilities.find(f => f.id === facilityId);
              this.selectedHospital = facilityId > 0 && selectedFacility
                ? selectedFacility
                : currentIDN.hospitalFacilities[0];

              this.dashboardFacilityName = this.selectedHospital?.name;
            }
            const userRole = localStorage.getItem('userRole');
            const userRoles = userRole?.split(',').map(role => Number(role.trim())) || [];

            const isClinicianOrAdmin = userRoles.includes(HSPARole.ClinicianUser) ||
              userRoles.includes(HSPARole.CustomerSystemAdministrator);

            const hasSingleFacility = currentIDN.hospitalFacilities?.length === 1;

            if (isClinicianOrAdmin && hasSingleFacility) {
              this.changeButtonVisibility = true;
            }


            this.updateUserSession(currentIDN);
            this.tenantCode = currentIDN.idmIdnCode;
          }
        } else {
          localStorage.removeItem('facilityId');
          this.updateUserSession(null);
        }

        this.loggingService.activityLog('Logged In successfully.');
        return this.navMenuService.setSessionInfo(this.createSessionInfo());
      })
    );

    this.navMenuSubscriptions.push(
      navMenu$.subscribe(() => {
        this.isLoading = false;
        if (this.selectedHospital?.id > 0) {
          localStorage.setItem('facilityId', this.selectedHospital.id.toString());
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.navMenuSubscriptions.forEach(sub => sub.unsubscribe());
  }

  signOut() {
    this.loggingService.activityLog("Logging out.");
    this.navMenuService.removeSessionInfo()
      .subscribe(() => {
        this.authorizeService.signOut();
      });
  }

  removeBlanks(str: string): string {
    return str.replace(/\s/g, '');
  }


  changeCulture(hospital: IFacility) {
    this.isLoading = true;
    this.selectedHospital = hospital;
    const currentIDN = this.allIdns.find(idn => idn.id == hospital.idnId);

    this.resourcesService.changeCulture(hospital.cultureId);
    this.selectedIdnID = currentIDN.id;
    this.facilityForm.get('idn')?.setValue(this.selectedIdnID);
    this.dashboardIDNName = currentIDN.name;
    this.dashboardFacilityName = this.selectedHospital.name;
    this.navMenuService.onNavMenuChange(true);

    this.navMenuService.setSessionInfo(this.createSessionInfo())
      .subscribe((response: any) => {
        this.updateUserSession(currentIDN);
        localStorage.setItem('tenantId', currentIDN.id.toString());
        localStorage.setItem('facilityId', this.selectedHospital.id.toString());
        this.router.navigate(['']);
        this.subMenuclick = MenuEnum.None;
        this.loggingService.activityLog("Switched to facility: " + this.selectedHospital.name);
        this.isLoading = false;
      })
  }

  private getUserSession(): void {
    this.userSessionService.getUserSession()
      .subscribe(session => {
        if (session) {
          this.tenantCode = session.idmIdnCode;
        }
      });
  }

  private updateUserSession(idn: IIDN | null): void {
    const userSession: IUserSession | null = idn
      ? {
        tenantId: localStorage.getItem('tenantId'),
        idnId: this.selectedHospital?.idnId || idn.id,
        idmIdnId: idn.idmIdnId,
        idmIdnCode: idn.idmIdnCode,
        idnName: idn.name,
        facilityId: this.selectedHospital?.id,
        facilityName: this.selectedHospital?.name
      }
      : null;

    this.userSessionService.setUserSession(userSession);
  }

  selectedIdnIDValue(idnID: number): void {
    const currentIDN = this.allIdns.find(idn => idn.id == idnID);

    if (currentIDN?.hospitalFacilities?.length) {
      this.hospitals = currentIDN.hospitalFacilities;
      this.selectedHospital = this.hospitals[0];
    } else {
      this.hospitals = [];
      this.selectedHospital = null!;
    }
    this.facilityForm.get('facilityId')?.setValue(this.selectedHospital);
  }

  private showErrorNotification(message: string, resource: string) {
    this.toastrService.error(message, resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  private setApplicationProductName(): void {
    this.applicationNameFinalLabel = this.resourceKeyValue?.ApplicationNameFinalLabel || '';
  }

  private getSelectedMenu(): void {
    this.navMenuSubscriptions.push(
      this.userMenuManagementService.getSelectedMenu().subscribe((menu: MenuEnum) => {
        this.subMenuclick = menu;
      })
    );
  }

  onDashboardBarChange(): void {
    this.isLoading = true;
    this.allIdns = [];
    this.hospitals = [];
    this.navMenuService.getIDNAndFacilitiesForCurrentUser()
      .subscribe((response: IIDN[]) => {
        if (!response?.length) {
          this.isLoading = false;
          return;
        }

        this.allIdns = response;
        const currentIDN = this.selectedIdnID > 0
          ? this.allIdns.find(idn => idn.id == this.selectedIdnID)
          : this.allIdns[0];

        this.facilityForm.get('idn')?.setValue(currentIDN?.id ?? null);

        if (currentIDN?.hospitalFacilities?.length) {
          this.hospitals = currentIDN.hospitalFacilities;

          const matchedHospital = this.selectedHospital
            ? this.hospitals.find(facility => facility.id == this.selectedHospital.id)
            : null;

          this.selectedHospital = matchedHospital || this.hospitals[0];
          this.facilityForm.get('facilityId')?.setValue(this.selectedHospital);
        }

        if (!this.hasIDNsAccessAndAdmin && this.allIdns.length > 0) {
          this.facilityForm.get('idn')?.disable();
        }

        this.isLoading = false;
      });
  }


  getUserManagementUrl(): void {
    //Get idmUrl from AppSettings
    const idmUrl = this.appSettings.idmAuthPortalUrl;
    const IDMSubUrl = this.clientID && this.tenantCode ? `${idmUrl}/dashboard/main/(dashboard:users)?client_id=${this.clientID}&TenantCode=${this.tenantCode}` : idmUrl;
    window.location.href = this.removeBlanks(IDMSubUrl);
  }

  private createSessionInfo(): ISessionInfo {
    return {
      userName: this.navMenu?.email || '',
      idnId: this.selectedIdnID,
      idnName: this.dashboardIDNName,
      facilityId: this.selectedHospital?.id || 0,
      facilityName: this.selectedHospital?.name || '',
      role: localStorage.getItem('userRole')
    };
  }
}
